import { supabase } from '../lib/supabase';
import { logAuditEvent } from './auditLogService';

/**
 * Plan configuration mapping
 */
const PLAN_CONFIG = {
  'starter_monthly': {
    priceId: 'price_starter_monthly', // Replace with actual Stripe Price ID
    credits: 3,
    planType: 'monthly',
    name: 'Starter Monthly'
  },
  'starter_annual': {
    priceId: 'price_starter_annual', // Replace with actual Stripe Price ID
    credits: 3,
    planType: 'annual',
    name: 'Starter Annual'
  },
  'professional_monthly': {
    priceId: 'price_professional_monthly', // Replace with actual Stripe Price ID
    credits: 10,
    planType: 'monthly',
    name: 'Professional Monthly'
  },
  'professional_annual': {
    priceId: 'price_professional_annual', // Replace with actual Stripe Price ID
    credits: 10,
    planType: 'annual',
    name: 'Professional Annual'
  },
  'single_case': {
    priceId: 'price_single_case', // Replace with actual Stripe Price ID
    credits: 1,
    amount: 99,
    name: 'Single Case'
  }
};

/**
 * Create Stripe Checkout Session for subscription
 */
export async function createSubscriptionCheckout(planId, userId, userEmail) {
  try {
    // Check if user is a test account
    const { data: userProfile } = await supabase
      ?.from('user_profiles')
      ?.select('is_test_account')
      ?.eq('id', userId)
      ?.single();
    
    // Skip Stripe checkout for test accounts
    if (userProfile?.is_test_account) {
      console.log('Test account - skipping Stripe checkout');
      return { 
        success: true, 
        isTestAccount: true,
        message: 'Test account - no payment required'
      };
    }
    
    const planConfig = PLAN_CONFIG?.[planId];
    
    if (!planConfig) {
      return { success: false, error: 'Invalid plan selected' };
    }

    const { data, error } = await supabase?.functions?.invoke('create-checkout-session', {
      body: {
        planKey: planId,
        userId,
        userEmail,
        successUrl: `${window.location?.origin}/client-dashboard/financial?tab=overview&checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location?.origin}/client-dashboard/financial?tab=overview&checkout=canceled`
      }
    });

    if (error) throw error;

    return { success: true, sessionUrl: data?.url, sessionId: data?.sessionId };
  } catch (error) {
    console.error('Create subscription checkout error:', error);
    return { success: false, error: error?.message || 'Failed to create checkout session' };
  }
}

/**
 * Create Stripe Checkout Session for one-time case purchase
 */
export async function createOneTimeCaseCheckout(userId, userEmail, quantity = 1) {
  try {
    // Check if user is a test account
    const { data: userProfile } = await supabase
      ?.from('user_profiles')
      ?.select('is_test_account')
      ?.eq('id', userId)
      ?.single();
    
    // Skip Stripe checkout for test accounts
    if (userProfile?.is_test_account) {
      console.log('Test account - skipping Stripe checkout');
      return { 
        success: true, 
        isTestAccount: true,
        message: 'Test account - no payment required'
      };
    }

    const qty = Math.max(1, Math.min(20, parseInt(quantity) || 1));
    
    const { data, error } = await supabase?.functions?.invoke('create-checkout-session', {
      body: {
        planKey: 'single_case',
        quantity: qty,
        userId,
        userEmail,
        successUrl: `${window.location?.origin}/client-dashboard/financial?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location?.origin}/client-dashboard/financial?checkout=canceled`
      }
    });

    if (error) throw error;

    return { success: true, sessionUrl: data?.url, sessionId: data?.sessionId };
  } catch (error) {
    console.error('Create one-time checkout error:', error);
    return { success: false, error: error?.message || 'Failed to create checkout session' };
  }
}

/**
 * Verify checkout session after redirect
 */
export async function verifyCheckoutSession(sessionId) {
  try {
    const { data, error } = await supabase?.functions?.invoke('verify-checkout-session', {
      body: { sessionId }
    });

    if (error) throw error;

    return { success: true, session: data };
  } catch (error) {
    console.error('Verify checkout session error:', error);
    return { success: false, error: error?.message || 'Failed to verify checkout session' };
  }
}

/**
 * Get user's payment history
 */
export async function getPaymentHistory(userId) {
  try {
    const { data, error } = await supabase?.from('payments')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return { success: true, payments: data || [] };
  } catch (error) {
    console.error('Get payment history error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Get user's subscription history
 */
export async function getSubscriptionHistory(userId) {
  try {
    const { data, error } = await supabase
      ?.from('subscription_history')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return { success: true, history: data || [] };
  } catch (error) {
    console.error('Get subscription history error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Cancel subscription
 */
export async function cancelSubscription(userId, cancelAtPeriodEnd = true) {
  try {
    const { data, error } = await supabase?.functions?.invoke('cancel-subscription', {
      body: { userId, cancelAtPeriodEnd }
    });

    if (error) throw error;

    logAuditEvent({ actionType: 'payment.subscription_cancelled', actionDescription: `Subscription cancellation requested`, entityType: 'user', entityId: userId, metadata: { cancelAtPeriodEnd }, severity: 'warning' }).catch(console.error);
    return { success: true, data };
  } catch (error) {
    console.error('Cancel subscription error:', error);
    return { success: false, error: error?.message || 'Failed to cancel subscription' };
  }
}

/**
 * Reactivate cancelled subscription
 */
export async function reactivateSubscription(userId) {
  try {
    const { data, error } = await supabase?.functions?.invoke('reactivate-subscription', {
      body: { userId }
    });

    if (error) throw error;

    logAuditEvent({ actionType: 'payment.subscription_reactivated', actionDescription: `Subscription reactivated`, entityType: 'user', entityId: userId, severity: 'warning' }).catch(console.error);
    return { success: true, data };
  } catch (error) {
    console.error('Reactivate subscription error:', error);
    return { success: false, error: error?.message || 'Failed to reactivate subscription' };
  }
}

/**
 * Upgrade/downgrade subscription
 */
export async function changeSubscriptionPlan(userId, newPlanId) {
  try {
    const planConfig = PLAN_CONFIG?.[newPlanId];
    
    if (!planConfig) {
      return { success: false, error: 'Invalid plan selected' };
    }

    const { data, error } = await supabase?.functions?.invoke('upgrade-subscription', {
      body: {
        userId,
        newPlanKey: newPlanId,
      }
    });

    if (error) throw error;

    logAuditEvent({ actionType: 'payment.subscription_plan_changed', actionDescription: `Subscription plan changed to ${newPlanId}`, entityType: 'user', entityId: userId, metadata: { newPlanId }, severity: 'warning' }).catch(console.error);
    return { success: true, data };
  } catch (error) {
    console.error('Change subscription plan error:', error);
    return { success: false, error: error?.message || 'Failed to change subscription plan' };
  }
}

/**
 * Submit enterprise contact form
 */
export async function submitEnterpriseContact(contactData) {
  try {
    // No .select() — non-admin users have no SELECT policy on enterprise_leads
    const { error } = await supabase?.from('enterprise_leads')?.insert({
        full_name: contactData?.fullName,
        email: contactData?.email,
        company_name: contactData?.companyName,
        phone: contactData?.phone,
        estimated_monthly_cases: contactData?.estimatedMonthlyCases,
        message: contactData?.message,
        source: 'pricing_page'
      });

    if (error) throw error;

    // Trigger email notification to admin
    await supabase?.functions?.invoke('send-enterprise-lead-notification', {
      body: { leadId: null, contactData }
    });

    return { success: true };
  } catch (error) {
    console.error('Submit enterprise contact error:', error);
    return { success: false, error: error?.message || 'Failed to submit contact form' };
  }
}

/**
 * Get user's payment methods
 */
export async function getPaymentMethods(userId) {
  try {
    const { data, error } = await supabase?.from('payment_methods')?.select('*')?.eq('user_id', userId)?.order('is_default', { ascending: false });

    if (error) throw error;

    return { success: true, paymentMethods: data || [] };
  } catch (error) {
    console.error('Get payment methods error:', error);
    return { success: false, error: error?.message };
  }
}

/**
 * Set default payment method
 */
export async function setDefaultPaymentMethod(userId, paymentMethodId) {
  try {
    const { data, error } = await supabase?.functions?.invoke('set-default-payment-method', {
      body: { userId, paymentMethodId }
    });

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Set default payment method error:', error);
    return { success: false, error: error?.message || 'Failed to set default payment method' };
  }
}

/**
 * Remove payment method
 */
export async function removePaymentMethod(userId, paymentMethodId) {
  try {
    const { data, error } = await supabase?.functions?.invoke('remove-payment-method', {
      body: { userId, paymentMethodId }
    });

    if (error) throw error;

    logAuditEvent({ actionType: 'payment.method_removed', actionDescription: `Payment method removed`, entityType: 'user', entityId: userId, metadata: { paymentMethodId }, severity: 'warning' }).catch(console.error);
    return { success: true, data };
  } catch (error) {
    console.error('Remove payment method error:', error);
    return { success: false, error: error?.message || 'Failed to remove payment method' };
  }
}

/**
 * Open Stripe Customer Billing Portal
 * Lets users manage their subscription, payment method, and invoices directly in Stripe.
 */
export async function createBillingPortalSession(userId) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const authHeader = session?.access_token
      ? { Authorization: `Bearer ${session.access_token}` }
      : {};

    const { data, error } = await supabase?.functions?.invoke('create-billing-portal', {
      body: {
        userId,
        returnUrl: `${window.location?.origin}/client-dashboard`,
      },
      headers: authHeader,
    });

    if (error) throw error;

    return { success: true, url: data?.url };
  } catch (error) {
    console.error('Create billing portal error:', error);
    return { success: false, error: error?.message || 'Failed to open billing portal' };
  }
}